import {
  alpha,
  Box,
  InputAdornment,
  TextFieldProps,
  Typography,
  useTheme,
} from "@mui/material";
import {
  KeyboardEvent,
  KeyboardEventHandler,
  RefObject,
  useEffect,
} from "react";
import { useFormContext } from "react-hook-form";
import { CreateWorkspaceFormInput } from "~/pages/space/workspaces/components/CreateWorkspace/CreateWorkspacePage";
import { KeyOutlined } from "@mui/icons-material";
import { WorkSpaceConditionValuesNameType } from "~/pages/space/workspaces/components/ConditionBuilder/types";
import { last } from "lodash-es";
import { ConditionTagBaseAutocomplete } from "~/components/ConditionBuilder/ConditionTagAutocomplete";
import { ConditionTagMenuItemsGroup } from "~/components/ConditionBuilder/ConditionTagAutocomplete/ConditionTagMenuItemsGroup";
import { ConditionTagBaseMenuList } from "~/components/ConditionBuilder/ConditionTagAutocomplete/ConditionTagBaseMenuList";
import { ConditionTagCheckboxMenuItem } from "~/components/ConditionBuilder/ConditionTagAutocomplete/ConditionTagCheckboxMenuItem";
import { ConditionTagOpenDropdownButton } from "~/components/ConditionBuilder/ConditionTagOpenDropdownButton/ConditionTagOpenDropdownButton";
import { ApostrophesIcon } from "~/icons";

interface WorkspaceConditionValuesAutocompleteProps {
  values: string[];
  name: WorkSpaceConditionValuesNameType;
  conditionFormDictionaryKey: string;
  options: Array<{ label: string; value: string }>;
  anchorRef: RefObject<HTMLButtonElement | null>;
  onDropdownSearchFieldKeyUp: KeyboardEventHandler<HTMLInputElement>;
  onDropdownSearchFieldChange: TextFieldProps["onChange"];
  isValuesDropdownOpen: boolean;
  isDictionaryKeySelected: boolean;
  onValuesDropdownOpen: () => void;
  onValuesDropdownClose: () => void;
  onItemClick: (value: unknown) => void;
  handleKeyValueKeyClick: (value: string) => void;
  handleKeyValueValueClick: (value: string) => void;
}

export const WorkspaceConditionValuesAutocomplete = ({
  values,
  name,
  conditionFormDictionaryKey,
  options,
  anchorRef,
  onDropdownSearchFieldKeyUp,
  onDropdownSearchFieldChange,
  onItemClick,
  isValuesDropdownOpen,
  isDictionaryKeySelected,
  onValuesDropdownOpen,
  onValuesDropdownClose,
  handleKeyValueKeyClick,
  handleKeyValueValueClick,
}: WorkspaceConditionValuesAutocompleteProps) => {
  const { register, trigger } = useFormContext<CreateWorkspaceFormInput>();
  const theme = useTheme();

  const lastItemLabel = last(options)?.label;

  const handleKeyDown = (
    event: KeyboardEvent<HTMLLIElement>,
    label: string,
  ): void => {
    // Check if `Tab` is pressed on the last item
    if (event.key === "Tab") {
      if (label === lastItemLabel && !event.shiftKey) {
        // Close dropdown if Tab is pressed on the last item
        onValuesDropdownClose();
      }
    }
  };

  useEffect(() => {
    register(name, {
      required: "This condition is incomplete. Please add a selection.",
    });
    trigger(name);
  }, [register]);

  return (
    <Box>
      <ConditionTagOpenDropdownButton
        isOpen={isValuesDropdownOpen}
        onClick={
          isValuesDropdownOpen ? onValuesDropdownClose : onValuesDropdownOpen
        }
        data-testid={`${isValuesDropdownOpen ? "close" : "open"}-values-dropdown`}
      />
      <Box sx={{ maxWidth: "400px" }}>
        {isDictionaryKeySelected ? (
          <ConditionTagBaseAutocomplete
            showKeyboardHint
            open={isValuesDropdownOpen}
            onClose={onValuesDropdownClose}
            onDropdownSearchFieldKeyUp={onDropdownSearchFieldKeyUp}
            anchorRef={anchorRef}
            onSearchInputChange={onDropdownSearchFieldChange}
            startAdornment={
              <>
                <InputAdornment
                  position="start"
                  onClick={() => {
                    if (!conditionFormDictionaryKey) {
                      handleKeyValueKeyClick("");
                    }
                  }}
                  sx={[
                    conditionFormDictionaryKey
                      ? {
                          height: "40px",
                          maxHeight: "unset",
                          marginLeft: "-8px",
                          cursor: "pointer",
                          borderBottomLeftRadius: "4px",
                          borderTopLeftRadius: "4px",
                          padding: "0 8px",
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.5,
                          ),
                          zIndex: 2,
                          border: "1px solid",
                          borderColor: theme.palette.primary.main,
                        }
                      : {},
                  ]}
                >
                  {conditionFormDictionaryKey ? (
                    <Typography fontWeight={700}>
                      {conditionFormDictionaryKey}
                    </Typography>
                  ) : (
                    <KeyOutlined sx={{ fontSize: "20px" }} />
                  )}
                </InputAdornment>
                {conditionFormDictionaryKey && (
                  <ApostrophesIcon sx={{ fontSize: "16px", mr: 1 }} />
                )}
              </>
            }
            renderContent={() => (
              <ConditionTagBaseMenuList sx={{ mt: 2 }}>
                <ConditionTagMenuItemsGroup
                  groupTitle={
                    conditionFormDictionaryKey
                      ? "SEARCH RESULTS FOR VALUES MATCHING THIS KEY"
                      : "SEARCH RESULTS FOR KEYS"
                  }
                  onClick={
                    conditionFormDictionaryKey
                      ? handleKeyValueValueClick
                      : handleKeyValueKeyClick
                  }
                  onKeyDown={handleKeyDown}
                  items={options}
                />
              </ConditionTagBaseMenuList>
            )}
          />
        ) : (
          <ConditionTagBaseAutocomplete
            showKeyboardHint
            open={isValuesDropdownOpen}
            onClose={onValuesDropdownClose}
            onDropdownSearchFieldKeyUp={onDropdownSearchFieldKeyUp}
            anchorRef={anchorRef}
            onSearchInputChange={onDropdownSearchFieldChange}
            renderContent={() => (
              <ConditionTagBaseMenuList sx={{ mt: 2 }}>
                {options.map((option) => (
                  <ConditionTagCheckboxMenuItem
                    label={option.label}
                    value={option.value}
                    isChecked={values.includes(option.value)}
                    onClick={onItemClick}
                    key={option.value}
                    onKeyDown={handleKeyDown}
                  />
                ))}
              </ConditionTagBaseMenuList>
            )}
          />
        )}
      </Box>
    </Box>
  );
};
